import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ListTree,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { ApiError, apiFetch } from '@/lib/api';

type CategoryKind = 'INCOME' | 'EXPENSE' | 'TRIBUTE' | 'DISTRIBUTION' | 'TRANSFER';

interface Category {
  id: string;
  codigo: string;
  nome: string;
  kind: CategoryKind;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
}

const KIND_LABEL: Record<CategoryKind, string> = {
  INCOME: 'Receita',
  EXPENSE: 'Despesa',
  TRIBUTE: 'Tributo',
  DISTRIBUTION: 'Distribuição',
  TRANSFER: 'Transferência',
};

const KIND_STYLE: Record<CategoryKind, string> = {
  INCOME: 'bg-emerald-500/10 text-emerald-600',
  EXPENSE: 'bg-amber-500/10 text-amber-600',
  TRIBUTE: 'bg-rose-500/10 text-rose-600',
  DISTRIBUTION: 'bg-indigo-500/10 text-indigo-600',
  TRANSFER: 'bg-slate-500/10 text-slate-600',
};

export function CategoriesPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Category | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiFetch<Category[]>('/categories'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/categories/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
    onError: (err) =>
      alert(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  // Ordena por codigo + agrupa raizes e filhos
  const sorted = [...(data ?? [])].sort((a, b) => a.codigo.localeCompare(b.codigo));
  const roots = sorted.filter((c) => !c.parentId);
  const childrenOf = (parentId: string) => sorted.filter((c) => c.parentId === parentId);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Plano de Contas</h1>
          <p className="text-sm text-muted-foreground">
            Estrutura hierárquica usada para classificar lançamentos financeiros
            e gerar o DRE.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nova categoria
          </button>
        </div>
      </header>

      <section className="rounded-lg border border-border bg-card">
        {isLoading && <p className="p-4 text-sm text-muted-foreground">Carregando…</p>}
        {data && data.length === 0 && (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <ListTree className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">
              Nenhuma categoria. Plano de contas será populado automaticamente.
            </p>
          </div>
        )}
        {data && data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/30 text-left">
              <tr>
                <th className="px-4 py-2 font-medium w-24">Código</th>
                <th className="px-4 py-2 font-medium">Nome</th>
                <th className="px-4 py-2 font-medium w-32">Tipo</th>
                <th className="px-4 py-2 font-medium w-20">Status</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {roots.flatMap((root) => [
                <CategoryRow key={root.id} cat={root} depth={0} onEdit={setEditing} onDelete={remove.mutate} />,
                ...childrenOf(root.id).map((child) => (
                  <CategoryRow
                    key={child.id}
                    cat={child}
                    depth={1}
                    onEdit={setEditing}
                    onDelete={remove.mutate}
                  />
                )),
              ])}
            </tbody>
          </table>
        )}
      </section>

      {showCreate && (
        <CategoryFormDialog
          parentOptions={roots}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['categories'] });
          }}
        />
      )}
      {editing && (
        <CategoryFormDialog
          category={editing}
          parentOptions={roots.filter((r) => r.id !== editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['categories'] });
          }}
        />
      )}
    </div>
  );

  function CategoryRow({
    cat,
    depth,
    onEdit,
    onDelete,
  }: {
    cat: Category;
    depth: number;
    onEdit: (c: Category) => void;
    onDelete: (id: string) => void;
  }) {
    return (
      <tr className="border-b border-border last:border-0 hover:bg-accent/30">
        <td className="px-4 py-2 font-mono text-xs">{cat.codigo}</td>
        <td className="px-4 py-2 font-medium" style={{ paddingLeft: `${1 + depth * 1.5}rem` }}>
          {depth > 0 && <span className="text-muted-foreground/40 mr-2">└</span>}
          {cat.nome}
          {cat.isSystem && (
            <span className="ml-2 inline-flex items-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              sistema
            </span>
          )}
        </td>
        <td className="px-4 py-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${KIND_STYLE[cat.kind]}`}
          >
            {KIND_LABEL[cat.kind]}
          </span>
        </td>
        <td className="px-4 py-2">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${
              cat.isActive ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-muted-foreground'
            }`}
          >
            {cat.isActive ? 'Ativo' : 'Inativo'}
          </span>
        </td>
        <td className="px-2 py-2 flex justify-end gap-1">
          <button
            type="button"
            data-dica="Editar"
            onClick={() => onEdit(cat)}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent"
          >
            <Pencil className="h-4 w-4" />
          </button>
          {!cat.isSystem && (
            <button
              type="button"
              data-dica="Excluir"
              onClick={() => {
                if (confirm(`Excluir categoria "${cat.nome}"?`)) onDelete(cat.id);
              }}
              className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </td>
      </tr>
    );
  }
}

// ============================ dialog ============================

function CategoryFormDialog({
  category,
  parentOptions,
  onClose,
  onSaved,
}: {
  category?: Category;
  parentOptions: Category[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!category;
  const [form, setForm] = useState({
    codigo: category?.codigo ?? '',
    nome: category?.nome ?? '',
    kind: category?.kind ?? ('EXPENSE' as CategoryKind),
    parentId: category?.parentId ?? '',
    sortOrder: category?.sortOrder ?? 0,
    isActive: category?.isActive ?? true,
  });
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify({
        ...form,
        parentId: form.parentId || undefined,
      });
      return isEdit
        ? apiFetch(`/categories/${category!.id}`, { method: 'PATCH', body })
        : apiFetch('/categories', { method: 'POST', body });
    },
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.body : err instanceof Error ? err.message : 'erro'),
  });

  function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    save.mutate();
  }

  return (
    <Dialog title={isEdit ? `Editar — ${category!.nome}` : 'Nova categoria'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-[120px_1fr] gap-3">
          <Field label="Código *">
            <input
              required
              className="input"
              placeholder="2.10"
              value={form.codigo}
              onChange={(e) => setForm((s) => ({ ...s, codigo: e.target.value }))}
            />
          </Field>
          <Field label="Nome *">
            <input
              required
              className="input"
              value={form.nome}
              onChange={(e) => setForm((s) => ({ ...s, nome: e.target.value }))}
            />
          </Field>
        </div>
        <Field label="Tipo *">
          <select
            required
            className="input"
            value={form.kind}
            onChange={(e) => setForm((s) => ({ ...s, kind: e.target.value as CategoryKind }))}
          >
            {(Object.keys(KIND_LABEL) as CategoryKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Categoria pai (opcional)">
          <select
            className="input"
            value={form.parentId}
            onChange={(e) => setForm((s) => ({ ...s, parentId: e.target.value }))}
          >
            <option value="">— Raiz (sem pai) —</option>
            {parentOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} — {p.nome}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
          />
          Ativa
        </label>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-input px-3 py-2 text-sm hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar'}
          </button>
        </div>
      </form>
      <Style />
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs font-medium">{label}</span>
      {children}
    </label>
  );
}

function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid hsl(var(--input));
        background: hsl(var(--background));
        padding: 0.5rem 0.75rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
      }
      .input:focus { outline: none; box-shadow: 0 0 0 2px hsl(var(--ring) / 0.4); }
    `}</style>
  );
}
